import React, { useCallback } from 'react';
import type { FC } from 'react';
import { useIntl } from 'react-intl-next';

import { SearchFooter } from '@atlassian/search-dialog';
import { fg } from '@atlaskit/platform-feature-flags';

import { messages } from '../../../messages';
import { SearchDialogProduct } from '../../product-router/product/search-dialog-product';
import { useProductContext } from '../../product-router/product-router';
import { useDefaultSuppliers } from '../../result-supplier/default-suppliers';
import { useJiraGraphQLSupplier } from '../../result-supplier/jira-graphql-supplier/jira-graphql-supplier';
import {
	type ExtensibleJiraTabProps,
	type CurrentProject,
	JIRA_PRODUCT_ID,
	type JiraScope,
	type SupportedJiraScopeResponses,
	type CurrentUser,
} from './types';
import { useGenericFilters } from '../../filters/filter-context/filter-context';
import { useFilters } from './filters';
import { useSections } from './sections';
import { useURLFactory } from './url-factory';
import { JiraAdvancedSearch } from './advanced-search';

import { onAdvancedSearchSelected } from '../../../common/analytics/events';
import { useSearchConfigContext } from '../../../common/search-config-provider/search-config-provider';
import { useSearchSessionId } from '../../../common/search-session-provider/search-session-provider';
import { useShowAtlassianHomeSearchLink } from '../../../common/show-atlassian-home-search-link';
import { useUserContext } from '../../../common/user-context/user-context';
import { defaultPermissionsSupplier } from './permission-supplier';
import { useDialogExpansionContext } from '../../dialog-expansion-context/dialog-expansion-context';
import { useQuery } from '../../query-context/query-context';
import { type JiraResultWithType, appendQueryParams } from './jira-analytics-query-params';
import SingleSiteAdvancedSearch from './single-site-advanced-search';
import { ProductStates } from '../../product-state-machine/product-state-machine-types';
import { LoadingSpinner } from '../../../common/loading-spinner/loading-spinner';
import { useABTest } from '../../../extensible/ab-test-context/ab-test-context';
import NoPreQueryResults from '../../no-results-screen/no-prequery-results';
import { useJiraDateFilter } from './filter-by-date/use-jira-date-filter';
import {
	AtlassianHomeSearchLink,
	getAtlassianHomeSearchUrl,
} from '../../advanced-search-footer/atlassian-home-search-link';
import { ReturnIconWithModifier } from '../../advanced-search-footer/advanced-search-footer';
import { type AdvancedSearchUrlGenerator } from '../../product-router/product-router-state';
import { useTypedAggregatorClient } from '../../aggregator-client-context/aggregator-client-context';
import { getSessionIdFromLocalStorage } from '../../utils/get-session-id';

let hasLoadedFilters = false;
let currentProjectRef: CurrentProject | undefined;
let currentUserRef: CurrentUser | undefined;

export const setHasLoadedFilters: (value: boolean) => boolean = (value: boolean) =>
	(hasLoadedFilters = value);

const JiraNoPreQueryResults = () => (
	<NoPreQueryResults message={messages.jira_empty_state_prequery_heading} />
);

export const ExtensibleJiraTab: FC<ExtensibleJiraTabProps> = ({
	features,
	children,
	config,
	formatDate,
	autocorrectEnabled,
	currentProject,
	currentUser,
	preQueryItemSupplier,
	postQueryItemSupplier,
	productEdition,
	productDataRegions,
	experimentConfig,
	isJiraIssueIndexed,
	...rest
}) => {
	const { formatMessage } = useIntl();
	const { jiraSites } = useSearchConfigContext();
	const { isExpanded } = useDialogExpansionContext();
	const { getActiveProduct } = useProductContext();
	const { query } = useQuery();
	const searchSessionId = useSearchSessionId();
	const hasAdvancedRoadmapsAccess = !!features?.hasAdvancedRoadmapsAccess;
	const hasSoftwareAccess = !!features?.hasSoftwareAccess;
	const isPreviewOnJiraSearchResultsEnabled = !!features?.isPreviewOnJiraSearchResultsEnabled;
	const isReportedByMeFilterEnabled = !!features?.isReportedByMeFilterEnabled;
	const isConsistentIssueDetailsEnabled = !!features?.isConsistentIssueDetailsEnabled;
	const isDateFromFilterEnabled = !!features?.enableDateFromFilter;
	const shouldShowIssueLabelsFilter = !!features?.enableIssueLabelsFilter;
	const isPreQueryFiltersEnabled = true;
	const isSearchWithTextFieldInAllIssuesLinkEnabled =
		!!features?.isSearchWithTextFieldInAllIssuesLinkEnabled;
	const productSessionId = getSessionIdFromLocalStorage();

	const { orgId } = useUserContext();
	const { getPrimaryProduct } = useProductContext();
	const primaryProduct = getPrimaryProduct();
	const aggregatorClient = useTypedAggregatorClient();
	const cloudId = aggregatorClient?.getAggregatorConfig().cloudId;
	const showAtlassianHomeSearchLink = !!(
		useShowAtlassianHomeSearchLink(primaryProduct?.id) &&
		orgId &&
		cloudId
	);
	const { defineFilters } = useGenericFilters();
	const { filtersDefinition, loadFilters, filterValueGetters } = useFilters(
		rest,
		shouldShowIssueLabelsFilter,
		config,
		currentProject,
		currentUser,
		isReportedByMeFilterEnabled,
	);
	const { jiraTabDateFilterDefinition, appliedDateFilterValue } = useJiraDateFilter({
		isDateFromFilterEnabled,
	});

	const fasterSearchDisabledFromCombinedExperiment = Boolean(
		experimentConfig?.combinedExperiment?.enabled &&
		experimentConfig?.combinedExperiment?.uiFeatures?.fasterSearchDisabled,
	);

	const abTest = useABTest();
	const isFasterSearchDisabled =
		fasterSearchDisabledFromCombinedExperiment ||
		(abTest !== null &&
			abTest !== undefined &&
			(abTest.experimentId === 'durian-without-faster-search' ||
				abTest.experimentId === 'default-without-faster-search'));

	if (
		(!hasLoadedFilters ||
			currentProjectRef?.id !== currentProject?.id ||
			currentUserRef?.id !== currentUser?.id ||
			currentUserRef?.avatarUrl !== currentUser?.avatarUrl) &&
		isExpanded &&
		getActiveProduct()?.id === JIRA_PRODUCT_ID &&
		(isPreQueryFiltersEnabled || query !== '')
	) {
		loadFilters();
		currentProjectRef = currentProject;
		currentUserRef = currentUser;
		setHasLoadedFilters(true);
	}

	defineFilters(JIRA_PRODUCT_ID, [
		...(isDateFromFilterEnabled ? jiraTabDateFilterDefinition : []),
		...filtersDefinition,
	]);

	const { getFiltersAdvancedSearchUrl } = useURLFactory(
		filterValueGetters,
		appliedDateFilterValue,
		isPreQueryFiltersEnabled,
		isSearchWithTextFieldInAllIssuesLinkEnabled,
	);

	const generateAdvancedSearchUrl: AdvancedSearchUrlGenerator = useCallback(
		(query, baseUrl, usage) => {
			if (usage === 'onInputEnterModified' && showAtlassianHomeSearchLink) {
				return getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct?.id);
			}
			return getFiltersAdvancedSearchUrl(query, baseUrl);
		},
		[getFiltersAdvancedSearchUrl, orgId, cloudId, showAtlassianHomeSearchLink, primaryProduct?.id],
	);

	const { recentSectionTitleGenerator, sections } = useSections(
		hasAdvancedRoadmapsAccess,
		hasSoftwareAccess,
		getFiltersAdvancedSearchUrl,
		isPreviewOnJiraSearchResultsEnabled,
		useIntl(),
		formatDate,
		isConsistentIssueDetailsEnabled,
		autocorrectEnabled,
	);

	const defaultSuppliers = useDefaultSuppliers<
		SupportedJiraScopeResponses,
		JiraScope,
		JiraResultWithType
	>(JIRA_PRODUCT_ID, sections, {
		preQueryItemSupplier,
		postQueryItemSupplier,
	});

	const jiraExperimentSuppliers = useJiraGraphQLSupplier({
		searchSessionId,
		sessionId: productSessionId,
		sections,
		cloudId,
		productEdition,
		productDataRegions,
		experimentConfig,
		isJiraIssueIndexed,
	});

	const isJiraGraphQLEnabled =
		fg('jira_enable_qs_graphql') && getActiveProduct()?.id === JIRA_PRODUCT_ID;

	const itemSuppliers = {
		preQueryItemSupplier: defaultSuppliers.preQueryItemSupplier,
		postQueryItemSupplier: isJiraGraphQLEnabled
			? jiraExperimentSuppliers.postQueryItemSupplier
			: defaultSuppliers.postQueryItemSupplier,
	};

	return (
		<SearchDialogProduct
			id={JIRA_PRODUCT_ID}
			expandedStateInputPlaceholder={formatMessage(messages.jira_search_placeholder)}
			title={formatMessage(messages.jira_tab_label)}
			autocorrectEnabled={autocorrectEnabled}
			sections={sections}
			preQuerySectionTitleGenerator={recentSectionTitleGenerator}
			generateAdvancedSearchUrl={generateAdvancedSearchUrl}
			{...rest}
			{...itemSuppliers}
			permissionSupplier={
				jiraSites.length > 0 ? defaultPermissionsSupplier : rest.permissionSupplier
			}
			appendQueryParams={appendQueryParams}
			isPreQueryFiltersEnabled={isPreQueryFiltersEnabled}
			debounceTime={300}
			isFasterSearchDisabled={Boolean(fasterSearchDisabledFromCombinedExperiment)}
		>
			{({ linkComponent, productState }) => {
				return {
					Body: () => {
						if (productState === ProductStates.PostQueryLoading && isFasterSearchDisabled) {
							return <LoadingSpinner />;
						} else if (productState === ProductStates.PreQueryNoResult) {
							return <JiraNoPreQueryResults />;
						}
					},
					SectionFooter:
						jiraSites.length > 1
							? undefined // don't show the footer for multi sites
							: () => (
									<SingleSiteAdvancedSearch
										advancedSearchSelected={onAdvancedSearchSelected}
										linkComponent={linkComponent}
										productState={productState}
										features={features}
									/>
								),
					Footer: () => (
						<>
							<JiraAdvancedSearch
								returnKeyIconComponent={
									showAtlassianHomeSearchLink ? () => <ReturnIconWithModifier /> : undefined
								}
								linkComponent={linkComponent}
								productState={productState}
								features={features}
							/>
							{showAtlassianHomeSearchLink && orgId && (
								<SearchFooter>
									<AtlassianHomeSearchLink orgId={orgId} cloudId={cloudId} showModifierIcon />
								</SearchFooter>
							)}
						</>
					),
				};
			}}
		</SearchDialogProduct>
	);
};
